import { useMemo, useState } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";

import { Box, IconButton, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { tokens } from "../../theme";

import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import SchoolIcon from "@mui/icons-material/School";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";

import { useAuth } from "../../AuthContext";
import { useRedfidViews } from "../../utils/redfidView";

const COLLAPSED_W = 80;
const EXPANDED_W = 280;
const SIDE_PADDING = 30;

const Item = ({
  title,
  to,
  href,
  icon,
  selected,
  setSelected,
  onNavigate,
  collapsed,
}) => {
  const colors = tokens();
  const isExternal = !!href && !to;

  return (
    <MenuItem
      active={selected === title}
      style={{ color: colors.primary[100] }}
      icon={icon}
      onClick={() => {
        setSelected(title);
        onNavigate?.();

        if (isExternal) {
          window.open(href, "_blank", "noopener,noreferrer");
        }
      }}
    >
      {!collapsed && <Typography>{title}</Typography>}
      {to ? <Link to={to} /> : null}
    </MenuItem>
  );
};

const Sidebar = () => {
  const auth = useAuth();
  const { data } = useRedfidViews();

  const showRegions = data?.country === "chile";

  const loggedUser = data?.raw.users?.find(
    (u) => String(u.id) === String(auth?.user?.user_id)
  );

  const isPrivileged = loggedUser?.is_admin || loggedUser?.is_staff;

  const colors = tokens();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("Panel de datos");

  const commonSx = {
    "& .pro-sidebar-inner": { background: `${colors.primary[200]} !important` },
    "& .pro-icon-wrapper": { backgroundColor: "transparent !important" },
    "& .pro-inner-item": {
      padding: open
        ? `5px ${SIDE_PADDING}px 5px ${SIDE_PADDING}px !important`
        : "5px 20px !important",
    },
    "& .pro-inner-item:hover": { color: `${colors.green[100]} !important` },
    "& .pro-menu-item.active": { color: `${colors.green[200]} !important` },
  };

  const miActividadHref = useMemo(() => {
    const base = String(process.env.REACT_APP_COMUNIDADES_URL ?? "").replace(/\/+$/, "");
    const userId = auth?.user?.user_id;
    if (!base || !userId) return "";
    return `${base}/user/${encodeURIComponent(String(userId))}`;
  }, [auth?.user?.user_id]);

  const linkedUser = useMemo(() => {
    const authUserId = String(auth?.user?.user_id ?? "").trim();
    const users = Array.isArray(data?.raw?.users) ? data.raw.users : [];

    return users.find((u) => String(u?.id ?? "").trim() === authUserId) || null;
  }, [data?.raw?.users, auth?.user?.user_id]);

  const displayName = useMemo(() => {
    if (isPrivileged) return "ADMIN";

    const firstName = String(linkedUser?.first_name ?? "").trim();
    const lastName = String(linkedUser?.last_name ?? "").trim();
    const fullName = [firstName, lastName].filter(Boolean).join(" ");

    return fullName || auth?.user?.username || "Formador";
  }, [isPrivileged, linkedUser, auth?.user?.username]);



  const Divider = ({ collapsed }) => (
    <Box
      sx={{
        height: "2px",
        backgroundColor: colors.green[200],
        opacity: 0.9,
        borderRadius: "999px",
        mx: collapsed ? "14px" : `${SIDE_PADDING}px`,
        my: "14px",
      }}
    />
  );

  const MenuContent = ({ collapsed }) => (
    <Menu iconShape="square">
      <MenuItem
        onClick={() => setOpen((v) => !v)}
        icon={collapsed ? <MenuOutlinedIcon /> : null}
        style={{ margin: "10px 0 10px 0", color: colors.green[200] }}
      >
        {!collapsed && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"

          >
            <Typography variant="h3" color={colors.primary[100]}>
              {displayName}
            </Typography>

            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              sx={{
                mr: "-8px",
                color: colors.primary[100],
                "&:hover": {
                  color: `${colors.green[100]} !important`,
                  backgroundColor: "transparent",
                },
              }}
            >
              <MenuOutlinedIcon />
            </IconButton>
          </Box>
        )}
      </MenuItem>

      {!collapsed && (
        <Box mb="25px" px={`${SIDE_PADDING}px`}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <img
              alt="logo"
              decoding="async"
              width="150px"
              height="auto"
              src="/assets/logo.png"
              style={{ display: "block" }}
            />
          </Box>
        </Box>
      )}

      <Box>
        <Item
          title="Panel de datos"
          to="/"
          icon={
            collapsed ? (
              <img
                alt="Panel de datos"
                decoding="async"
                src="/assets/logo.png"
                style={{ width: 30, height: 30, borderRadius: "20%" }}
              />
            ) : (
              <HomeOutlinedIcon />
            )
          }
          selected={selected}
          setSelected={setSelected}
          onNavigate={() => setOpen(false)}
          collapsed={collapsed}
        />

        <Divider collapsed={collapsed} />

        {isPrivileged ? (
          <Item
            title="Participantes"
            to="/participantes"
            icon={<PeopleOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
            onNavigate={() => setOpen(false)}
            collapsed={collapsed}
          />
        ) : (
          <Item
            title="Mi perfil"
            href={miActividadHref || (process.env.REACT_APP_COMUNIDADES_URL ?? "")}
            icon={<PersonOutlineOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
            onNavigate={() => setOpen(false)}
            collapsed={collapsed}
          />
        )}

        <Item
          title="Universidades"
          to="/universidades"
          icon={<AccountBalanceOutlinedIcon />}
          selected={selected}
          setSelected={setSelected}
          onNavigate={() => setOpen(false)}
          collapsed={collapsed}
        />


        <Item
          title="Actividades"
          to="/actividades"
          icon={<TodayOutlinedIcon />}
          selected={selected}
          setSelected={setSelected}
          onNavigate={() => setOpen(false)}
          collapsed={collapsed}
        />

        <Item
          title="Formación"
          to="/formacion"
          icon={<SchoolIcon />}
          selected={selected}
          setSelected={setSelected}
          onNavigate={() => setOpen(false)}
          collapsed={collapsed}
        />
        {showRegions ? (
          <Item
            title="Regiones"
            to="/regiones"
            icon={<MapOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
            onNavigate={() => setOpen(false)}
            collapsed={collapsed}
          />
        ) : null}

      </Box>
    </Menu>
  );

  return (
    <>
      <Box
        sx={{
          width: COLLAPSED_W,
          flexShrink: 0,
          ...commonSx,
          "& .pro-sidebar": {
            position: "fixed",
            left: 0,
            top: 0,
            height: "100vh",
            width: `${COLLAPSED_W}px`,
            zIndex: 1100,
          },
        }}
      >
        <ProSidebar collapsed>
          <MenuContent collapsed />
        </ProSidebar>
      </Box>

      {open && (
        <Box
          onClick={() => setOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.55)",
            zIndex: 1200,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "fixed",
              left: 0,
              top: 0,
              height: "100vh",
              width: EXPANDED_W,
              ...commonSx,
              "& .pro-sidebar": {
                height: "100vh",
                width: `${EXPANDED_W}px`,
              },
            }}
          >
            <ProSidebar collapsed={false}>
              <MenuContent collapsed={false} />
            </ProSidebar>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Sidebar;